import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useRouter } from 'next/router'
import {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  EmailPasswordSignIn,
  MARKETING_NEWS
} from '../../../components/auth/email-password-sign-in'
import { HeaderAuth } from '../../../components/auth/header-auth'
import { SignInProviders } from '../../../components/auth/sign-in-providers'
import { SocialSignIn } from '../../../components/auth/social-sign-in'
import { Button } from '../../../components/button'
import { Loading } from '../../../components/loading'
import { Title } from '../../../components/title'
import { useAuthentication } from '../../../hooks/auth/use-authentication'
import { getUserQuery } from '../../../hooks/user/use-get-user-data/get-user-query'
import { getTempAnonymousUserAuthToken } from '../../../lib/cli/user'
import { FirebaseAuth } from '../../../lib/firebase'
import { replacePlaceholders } from '../../../lib/misc/replace-placeholders'
import { ContextShell } from '../../context'
import styles from './login.module.scss'

const UTM_PARAMS_KEY = 'moises:utmParams'

function removeLeadingSlash(str: any): string {
  if (str && str?.startsWith('/')) {
    return str?.substring(1)
  }
  return str
}

export const Login = (): ReactElement => {
  const { i18n } = useLingui()
  const { query, push } = useRouter()
  const eventFired = useRef(false)
  const isAnonymousPairSessionRef = useRef(false)
  const [anonymousAuthLoading, setAnonymousAuthLoading] = useState(false)
  const {
    MoisesCLI,
    event: {
      sendEvent,
      sendEventPostHog,
      rudderstack: { identify: rudderstackIdentify }
    },
    userToken
  } = useContext(ContextShell)

  const pairCode = useMemo(() => {
    const redirect = query?.redirect as string
    if (!redirect || !redirect.includes('/pair')) return null
    try {
      const params = new URLSearchParams(redirect.split('?')[1])
      return params.get('code') || null
    } catch {
      return null
    }
  }, [query?.redirect])

  const [isSignIn, setIsSignIn] = useState(
    query && !Object.prototype.hasOwnProperty.call(query, 'signup')
  )

  const [isReferral] = useState(
    (query &&
      query?.redirect &&
      query.redirect.includes('claim-referral-link')) ||
      false
  )

  const [isCouponRedemption] = useState(query && query?.coupon)

  const isReferralPage = !isSignIn && isReferral

  const {
    error,
    loading,
    email,
    password,
    errorEmail,
    errorPassword,
    signInProviders,
    onChangeEmail,
    onFocusEmail,
    onChangePassword,
    signInApple,
    signInGoogle,
    signInTwitter,
    signInFacebook,
    signInEmail,
    signUpEmail,
    onBlur
  } = useAuthentication({
    isSignIn,
    isAnonymousUser: isAnonymousPairSessionRef.current
  })

  const utmParams = useMemo(() => {
    if (query && Object?.keys(query)?.length > 0) {
      return Object?.keys(query)?.reduce<{ [key: string]: any }>((acc, key) => {
        if (key?.startsWith('utm_')) {
          acc[key] = query?.[key]
        }
        return acc
      }, {})
    }

    return {}
  }, [query])

  const onGoResetPassword = useCallback(() => push('/reset-password'), [push])

  const toggleSignIn = useCallback(() => {
    if (!isSignIn) {
      sendEventPostHog({
        name: 'new_account_flow:sign_up_page_login_button_click',
        event_category: 'growth',
        customAttributes: {
          created_at: new Date().toISOString(),
          source: query?.source
        }
      })
    }

    setIsSignIn(!isSignIn)
  }, [isSignIn, sendEventPostHog, query?.source])

  const shouldSetUtmOnSignup = useCallback(
    async (eventName: string): Promise<void> => {
      try {
        if (eventName === 'logged_in') return // ignore for login

        const storedUtmParams = localStorage.getItem(UTM_PARAMS_KEY)
        if (!storedUtmParams) return

        const parsedUtmParams = JSON.parse(storedUtmParams)
        if (!parsedUtmParams || Object.keys(parsedUtmParams).length === 0) {
          localStorage.removeItem(UTM_PARAMS_KEY)
          return
        }

        const { user: userData } = await MoisesCLI.graphQL({
          query: getUserQuery as any
        })

        if (userData?.userInfo?.hasUtmOnSignup === false) {
          await MoisesCLI.setUtmOnSignup({
            utmOnSignup: parsedUtmParams
          })
        }

        localStorage.removeItem(UTM_PARAMS_KEY)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to set UTM on signup')
      }
    },
    [MoisesCLI]
  )

  const assignOnboardingExperiment = useCallback(
    async (
      eventName: string,
      redirect: any,
      onboardingExperimentKey: string
    ): Promise<string | null> => {
      const storedOnboardingExperiment = window.localStorage.getItem(
        onboardingExperimentKey
      )
      if (eventName === 'logged_in') return null // ignore for login
      if (storedOnboardingExperiment) return null // ignore if already assigned
      if (redirect) return null // don't assign experiment if redirecting

      let result = null
      try {
        const { value } = await MoisesCLI.assignExperiment({
          flag: 'webonboardingflow',
          personProperties: {}
        })
        window.localStorage.setItem(onboardingExperimentKey, value as string)
        result = value as string
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to assign onboarding experiment', err)
      }
      return result
    },
    [MoisesCLI]
  )

  const onAuthenticated = useCallback(
    async (user: any) => {
      if (!user) return

      // Skip redirect for anonymous pair session users without linked providers
      if (!user.providerData || user.providerData.length === 0) {
        return
      }

      const { redirect, ...urlParams } = query
      const { currentUser } = FirebaseAuth
      const { createdAt } = currentUser?.metadata as any
      const accountRecentlyCreated = Date.now() - createdAt < 5000
      const eventName = accountRecentlyCreated ? 'signed_up' : 'logged_in'
      const onboardingExperimentKey = 'moises:webonboardingflow'
      const token = userToken || (await user.getIdToken(true))
      MoisesCLI.auth(token)

      localStorage.setItem(UTM_PARAMS_KEY, JSON.stringify(utmParams))

      if (eventFired.current || !user?.uid) return
      eventFired.current = true
      rudderstackIdentify(user.uid)
      sendEvent({
        userId: user.uid,
        name: eventName,
        category: eventName === 'logged_in' ? 'engagement' : 'activation',
        customAttributes: {
          authentication: user?.providerData[0]?.providerId || 'not set',
          ...(isReferralPage && {
            referralId: (query?.redirect as string)?.replace(
              /^\/claim-referral-link\/|\/$/g,
              ''
            )
          })
        }
      })

      const provider = {
        'apple.com': 'apple',
        'google.com': 'google',
        'facebook.com': 'facebook',
        'twitter.com': 'x',
        password: 'email'
      } as { [key: string]: string }

      await new Promise((resolve) => setTimeout(resolve, 1000))

      const [, onboardingExperiment] = await Promise.all([
        shouldSetUtmOnSignup(eventName),
        assignOnboardingExperiment(eventName, redirect, onboardingExperimentKey)
      ])

      await new Promise((resolve) => setTimeout(resolve, 2500))

      sendEventPostHog({
        name:
          eventName === 'logged_in'
            ? 'authentication:sign_in_complete'
            : 'new_account_flow:sign_up_complete',
        event_category: 'growth',
        customAttributes: {
          created_at: new Date().toISOString(),
          userId: user.uid,
          source: query?.source,
          ...(eventName === 'logged_in'
            ? {
                sign_in_method:
                  provider[user?.providerData[0]?.providerId] || 'other'
              }
            : {
                signup_method:
                  provider[user?.providerData[0]?.providerId] || 'other'
              })
        }
      })

      const shouldUpdateUserComunication =
        localStorage.getItem(MARKETING_NEWS) === 'true'

      if (shouldUpdateUserComunication) {
        try {
          await MoisesCLI.updateUserComunication({
            updates: {
              email: true,
              push: false
            },
            activity: {
              email: true,
              push: true
            },
            collaboration: {
              email: true,
              push: true
            }
          })

          localStorage.removeItem(MARKETING_NEWS)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('Failed to update user communication')
        }
      }

      const params = new URLSearchParams(
        JSON.parse(JSON.stringify(urlParams))
      ).toString()

      const hasParams =
        params.length > 0
          ? `${redirect?.includes('?') ? '&' : '?'}${params}`
          : ''

      const redirectExperiment: Record<string, string> = {
        control: '/library',
        onboarding: '/onboarding?source=webonboardingflow'
      }

      window.location.href = redirect
        ? `/${removeLeadingSlash(redirect)}${hasParams}`
        : onboardingExperiment
        ? redirectExperiment[onboardingExperiment]
        : '/library'
    },
    [
      query,
      utmParams,
      rudderstackIdentify,
      sendEvent,
      isReferralPage,
      userToken,
      MoisesCLI,
      assignOnboardingExperiment,
      shouldSetUtmOnSignup,
      sendEventPostHog
    ]
  )

  useEffect(() => {
    const unsubscribe = FirebaseAuth.onIdTokenChanged(onAuthenticated)

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [onAuthenticated])

  // Handle anonymous pair session: fetch temp token and sign in with custom token
  useEffect(() => {
    if (!pairCode) return

    const authenticateAnonymous = async (): Promise<void> => {
      setAnonymousAuthLoading(true)
      try {
        const token = await getTempAnonymousUserAuthToken(pairCode)
        if (token) {
          isAnonymousPairSessionRef.current = true
          await FirebaseAuth.signInWithCustomToken(token)
        }
      } catch {
        // fail silently
      } finally {
        setAnonymousAuthLoading(false)
      }
    }

    authenticateAnonymous()
  }, [pairCode])

  const pageTitle = useMemo(() => {
    if (isReferralPage) {
      return replacePlaceholders(t`refer_friend_invitation`, {
        user: query?.user || '',
        plan: query?.plan || ''
      })
    }

    if (isCouponRedemption) {
      return replacePlaceholders(t`signup_or_login_coupon_redemption`, {
        coupon: query?.coupon || ''
      })
    }

    if (!isSignIn) {
      return t`actions.signup`
    }

    return t`actions.login`
  }, [
    isReferralPage,
    isSignIn,
    isCouponRedemption,
    query?.plan,
    query.user,
    query?.coupon
  ])

  const [sentEventSignUpPageView, setSentEventSignUpPageView] = useState(false)

  useEffect(() => {
    if (!isSignIn && !sentEventSignUpPageView) {
      setSentEventSignUpPageView(true)
      sendEventPostHog({
        name: 'new_account_flow:sign_up_page_view',
        event_category: 'growth',
        customAttributes: {
          created_at: new Date().toISOString(),
          source: query?.source
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignIn, sentEventSignUpPageView])

  return (
    <div className={styles.page}>
      <HeaderAuth isSignIn={isSignIn} toggleSignIn={toggleSignIn} />

      <div className={styles.container}>
        {!isSignIn ? (
          <div className={styles.aside}>
            <div className={styles.artistImageContainer}>
              <img
                className={styles.artistImage}
                alt={styles.artistLabel}
                src="//storage.googleapis.com/scala-web/assets/images/artists/eloy-casagrande-moises.png"
              />
            </div>
            <p className={styles.artistDescription}>
              {i18n._(t`eloy_quote_text`)}
            </p>
            <div className={styles.artistQuote}>
              <p className={styles.artistName}>
                {i18n._(t`eloy_quote_signature`)}
              </p>
              <p className={styles.artistLabel}>{i18n._(t`eloy_quote_end`)}</p>
            </div>
          </div>
        ) : null}

        <div className={styles.content}>
          {anonymousAuthLoading ? (
            <Loading />
          ) : (
            <>
              <Title className={styles.title}>{pageTitle}</Title>

              <div className={styles.form}>
                <p className={styles.subtitle}>
                  {isSignIn
                    ? i18n._(t`auth.signin.label`)
                    : i18n._(t`auth.signup.label`)}
                </p>

                <SocialSignIn
                  isSignIn={isSignIn}
                  className={styles.actions}
                  signInApple={signInApple}
                  signInGoogle={signInGoogle}
                  signInTwitter={signInTwitter}
                  signInFacebook={signInFacebook}
                />

                <p className={styles.subtitle}>
                  {isSignIn
                    ? i18n._(t`auth.signin.login`)
                    : i18n._(t`auth.signup.email`)}
                </p>

                <div className={styles.inputs}>
                  <EmailPasswordSignIn
                    email={email}
                    password={password}
                    error={error}
                    loading={loading}
                    isSignIn={isSignIn}
                    isReferral={isReferralPage}
                    errorEmail={errorEmail}
                    errorPassword={errorPassword}
                    onFocusEmail={onFocusEmail}
                    onChangeEmail={onChangeEmail}
                    onChangePassword={onChangePassword}
                    onBlur={onBlur}
                    onSubmit={isSignIn ? signInEmail : signUpEmail}
                  />

                  {signInProviders.show && (
                    <SignInProviders
                      {...signInProviders}
                      onChangeEmail={onChangeEmail}
                      signInApple={signInApple}
                      signInGoogle={signInGoogle}
                      signInTwitter={signInTwitter}
                      signInFacebook={signInFacebook}
                    />
                  )}
                </div>

                {isSignIn ? (
                  <div className={styles.containerResetPassword}>
                    <Button
                      small
                      radius={1}
                      transparent
                      id="forgot_my_password_button"
                      title={i18n._(t`actions.password.forgot`)}
                      onClick={onGoResetPassword}
                      className={styles.buttonResetPassword}
                    />
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
